import { useCallback, useEffect, useRef, useState } from "react";
import { format } from "date-fns";
import { BadgeCheck, FileText, ImagePlus, Pencil, RotateCcw, Wallet, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PAYMENT_METHODS, PAYMENT_STATUS_META, PAYMENT_TYPE_LABELS, REFUND_STATUS_META, fmtIDR } from "@/lib/constants";
import { NumInput } from "@/components/NumInput";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import InvoiceViewer from "@/components/InvoiceViewer";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";

const CREDIT_TYPE_LABELS = {
  CREDIT_CREATED: "Credit created",
  CREDIT_USED: "Credit applied to order",
  REFUND_TO_CREDIT: "Refund to credit",
  OWNER_ADJUSTMENT: "Owner adjustment",
  REVERSAL: "Reversal",
};

export default function OrderFinance({ order }) {
  const { user } = useAuth();
  const isOwner = user.role === "owner";
  const canRecord = ["owner", "admin_input"].includes(user.role);
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [credit, setCredit] = useState(null);
  const [refunds, setRefunds] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({ amount: "", method: "", reference: "", paid_at: new Date().toISOString().slice(0, 10), payment_type: "ADDITIONAL", notes: "" });
  const [dupWarning, setDupWarning] = useState("");
  const [summary, setSummary] = useState(null);
  const [shipOpen, setShipOpen] = useState(false);
  const [shipAmt, setShipAmt] = useState("");
  const [voidingId, setVoidingId] = useState(null);
  const [voidReason, setVoidReason] = useState("");
  const [viewInvoice, setViewInvoice] = useState(null);
  const [correctingId, setCorrectingId] = useState(null);
  const [corrForm, setCorrForm] = useState({ amount: "", method: "", payment_type: "ADDITIONAL", paid_at: "", reference: "", reason: "" });
  const [rejectingId, setRejectingId] = useState(null);
  const [corrRejectReason, setCorrRejectReason] = useState("");
  const [creditOpen, setCreditOpen] = useState(false);
  const [creditAmt, setCreditAmt] = useState("");
  const [refOpen, setRefOpen] = useState(false);
  const [refForm, setRefForm] = useState({ amount: "", reason: "", to_credit: true });
  const [rejectingRefund, setRejectingRefund] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);
  const proofRef = useRef(null);
  const proofFor = useRef(null);

  const load = useCallback(async () => {
    const [p, iv, cr, rf, sm] = await Promise.allSettled([
      api.get(`/orders/${order.id}/payments`),
      api.get(`/orders/${order.id}/invoices`),
      api.get(`/customers/${order.customer_id}/credit`),
      api.get("/refunds", { params: { customer_id: order.customer_id } }),
      api.get(`/orders/${order.id}/finance-summary`),
    ]);
    if (p.status === "fulfilled") setPayments(p.value.data);
    if (iv.status === "fulfilled") setInvoices(iv.value.data);
    if (cr.status === "fulfilled") setCredit(cr.value.data);
    if (rf.status === "fulfilled") setRefunds(rf.value.data.filter((r) => r.order_id === order.id));
    if (sm.status === "fulfilled") setSummary(sm.value.data);
    if ([p, iv, cr, rf].every((r) => r.status === "rejected")) toast.error(apiError(p.reason));
  }, [order.id, order.customer_id]);

  useEffect(() => {
    load();
  }, [load]);

  const latest = invoices.length > 0 ? invoices.reduce((a, b) => (b.version > a.version ? b : a)) : null;

  const recordPayment = async (force = false) => {
    setBusy(true);
    try {
      await api.post(`/orders/${order.id}/payments`, {
        amount: Number(payForm.amount),
        method: payForm.method.trim(),
        reference: payForm.reference.trim() || null,
        paid_at: payForm.paid_at,
        payment_type: payForm.payment_type,
        notes: payForm.notes.trim() || null,
        confirm_duplicate: force,
      });
      toast.success("Payment recorded — Finance checked externally");
      setPayOpen(false);
      setDupWarning("");
      setPayForm({ amount: "", method: "", reference: "", paid_at: new Date().toISOString().slice(0, 10), payment_type: "ADDITIONAL", notes: "" });
      load();
    } catch (e) {
      if (e?.response?.status === 409) setDupWarning(apiError(e));
      else toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const openCorrection = (p) => {
    setCorrectingId(p.id);
    setCorrForm({
      amount: String(p.amount),
      method: p.method || "",
      payment_type: p.payment_type || "ADDITIONAL",
      paid_at: p.paid_at || "",
      reference: p.reference || "",
      reason: "",
    });
  };

  const submitCorrection = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/payments/${correctingId}/correction`, {
        amount: Number(corrForm.amount),
        method: corrForm.method,
        payment_type: corrForm.payment_type,
        paid_at: corrForm.paid_at || null,
        reference: corrForm.reference.trim() || null,
        reason: corrForm.reason.trim(),
      });
      toast.success(data.status === "applied" ? "Payment corrected" : "Correction sent to the Owner for approval");
      setCorrectingId(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const approveCorrection = async (id) => {
    setBusy(true);
    try {
      await api.post(`/payments/${id}/correction/approve`);
      toast.success("Correction approved — original entry voided, corrected payment recorded");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const rejectCorrection = async () => {
    if (!corrRejectReason.trim()) return;
    setBusy(true);
    try {
      await api.post(`/payments/${rejectingId}/correction/reject`, { reason: corrRejectReason.trim() });
      toast.success("Correction rejected — original payment unchanged");
      setRejectingId(null);
      setCorrRejectReason("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const voidPayment = async () => {
    if (!voidReason.trim()) return;
    setBusy(true);
    try {
      await api.post(`/payments/${voidingId}/void`, { reason: voidReason.trim() });
      toast.success("Payment voided — original entry kept in history");
      setVoidingId(null);
      setVoidReason("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const saveInitialShipping = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/orders/${order.id}/shipping-charge`, { initial_shipping_charge: Number(shipAmt) || 0 });
      if ((data?.initial_shipping_charge ?? null) !== (Number(shipAmt) || 0)) {
        toast.error("Shipping Fee was not saved — please try again");
        return;
      }
      toast.success(`Shipping Fee saved — ${fmtIDR(Number(shipAmt) || 0)}`);
      setShipOpen(false);
      setShipAmt("");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const uploadProof = async (file) => {
    if (!file || !proofFor.current) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/payments/${proofFor.current}/proof`, fd);
      toast.success("Payment proof uploaded");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
      proofFor.current = null;
    }
  };

  const verifyPayment = async (id) => {
    setBusy(true);
    try {
      await api.post(`/payments/${id}/verify`);
      toast.success("Payment verified");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const rejectPayment = async (id) => {
    setBusy(true);
    try {
      await api.post(`/payments/${id}/reject`);
      toast.success("Payment rejected");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const generateInvoice = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/invoices`, {});
      toast.success(`Invoice V${data.version} generated — total ${fmtIDR(data.total)}`);
      setViewInvoice(data);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const applyCredit = async () => {
    setBusy(true);
    try {
      await api.post(`/orders/${order.id}/apply-credit`, { amount: Number(creditAmt) });
      toast.success("Credit applied to this order");
      setCreditOpen(false);
      setCreditAmt("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const requestRefund = async () => {
    setBusy(true);
    try {
      await api.post("/refunds", {
        customer_id: order.customer_id,
        order_id: order.id,
        amount: Number(refForm.amount),
        reason: refForm.reason.trim(),
        to_credit: refForm.to_credit,
      });
      toast.success("Refund requested (pending Owner approval)");
      setRefOpen(false);
      setRefForm({ amount: "", reason: "", to_credit: true });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const approveRefund = async (id) => {
    setBusy(true);
    try {
      await api.post(`/refunds/${id}/approve`);
      toast.success("Refund approved");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const rejectRefund = async () => {
    if (!rejectReason.trim()) return;
    setBusy(true);
    try {
      await api.post(`/refunds/${rejectingRefund}/reject`, { reason: rejectReason });
      toast.success("Refund rejected");
      setRejectingRefund(null);
      setRejectReason("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-5" data-testid="order-finance">
      <h2
        className="mb-2 rounded-md px-3 py-2 font-heading text-sm font-bold uppercase tracking-wide"
        style={{ backgroundColor: "#D8C9BE", color: "#4E4542" }}
      >
        Payments &amp; Invoice
      </h2>

      <div className="rounded-md border border-[#C7D5DB] p-4" style={{ backgroundColor: "#DCE5E9" }} data-testid="finance-summary">
        {summary && (
          <div className="space-y-1 text-xs" data-testid="finance-live-summary">
            <p className="flex justify-between"><span className="text-muted-foreground">Items subtotal</span><span className="font-semibold" data-testid="finance-subtotal">{fmtIDR(summary.subtotal)}</span></p>
            <p className="flex items-center justify-between"><span className="text-muted-foreground">Shipping Fee</span>
              <span className="flex items-center gap-1.5">
                <span className="font-semibold" data-testid="finance-shipping">{fmtIDR(summary.shipping_charge)}</span>
                {canRecord && (
                  <button
                    type="button"
                    data-testid="edit-shipping-fee-button"
                    aria-label="Edit shipping fee"
                    onClick={() => { setShipAmt(String(summary?.initial_shipping_charge || "")); setShipOpen(true); }}
                    className="flex h-7 w-7 items-center justify-center rounded-md text-stone-400 transition-colors hover:bg-accent hover:text-primary"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                )}
              </span>
            </p>
            {summary.insurance_amount > 0 && (
              <p className="flex justify-between"><span className="text-muted-foreground">Insurance</span><span className="font-semibold">{fmtIDR(summary.insurance_amount)}</span></p>
            )}
            <p className="flex justify-between"><span className="text-muted-foreground">Order total</span><span className="font-bold" data-testid="finance-order-total">{fmtIDR(summary.order_total)}</span></p>
            <p className="flex justify-between"><span className="text-muted-foreground">Total Payment Received</span><span className="font-semibold text-emerald-700" data-testid="finance-recorded-total">{fmtIDR(summary.recorded_payments)}</span></p>
            {summary.credit_used > 0 && (
              <p className="flex justify-between"><span className="text-muted-foreground">Credit applied</span><span className="font-semibold">{fmtIDR(summary.credit_used)}</span></p>
            )}
            <p className="flex justify-between border-t pt-1 text-sm">
              <span className="font-bold">Outstanding balance</span>
              <span className={`font-heading font-extrabold ${summary.outstanding > 0 ? "text-amber-700" : "text-emerald-700"}`} data-testid="finance-outstanding">
                {fmtIDR(summary.outstanding)}
              </span>
            </p>
          </div>
        )}
        {latest ? (
          <div className="mt-3 space-y-1 border-t pt-3 text-xs">
            {summary && (latest.paid_verified !== summary.recorded_payments || latest.credit_used !== summary.credit_used || latest.total !== summary.order_total) && (
              <p className="mb-1 rounded-md border border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] font-semibold text-amber-800" data-testid="invoice-stale-hint">
                Payments or credit changed since invoice V{latest.version} — tap "Generate Updated Invoice" to issue a new version
              </p>
            )}
            <p className="flex justify-between"><span className="text-muted-foreground">Invoice V{latest.version} total</span><span className="font-bold" data-testid="finance-invoice-total">{fmtIDR(latest.total)}</span></p>
            <p className="flex justify-between"><span className="text-muted-foreground">Payment Received</span><span className="font-semibold text-emerald-700" data-testid="finance-paid">{fmtIDR(latest.paid_verified)}</span></p>
            {latest.credit_used > 0 && (
              <p className="flex justify-between"><span className="text-muted-foreground">Credit applied</span><span className="font-semibold">{fmtIDR(latest.credit_used)}</span></p>
            )}
            <p className="flex justify-between border-t pt-1 text-sm">
              <span className="font-bold">Invoice balance due</span>
              <span className={`font-heading font-extrabold ${latest.balance_due > 0 ? "text-amber-700" : "text-emerald-700"}`} data-testid="finance-balance-due">
                {fmtIDR(latest.balance_due)}
              </span>
            </p>
          </div>
        ) : (
          <p className="mt-3 border-t pt-3 text-xs text-muted-foreground" data-testid="finance-no-invoice">No invoice generated yet</p>
        )}
        {credit && (
          <p className="mt-2 flex justify-between border-t pt-2 text-xs">
            <span className="text-muted-foreground">Customer credit balance</span>
            <span className="font-bold text-rose-700" data-testid="finance-credit-balance">{fmtIDR(credit.balance)}</span>
          </p>
        )}
      </div>

      {(canRecord || isOwner) && (
        <div className="mt-2 grid grid-cols-2 gap-2">
          {canRecord && (
            <Button variant="outline" data-testid="record-payment-button" className="h-11" onClick={() => setPayOpen(true)}>
              <Wallet className="h-4 w-4" /> Payment
            </Button>
          )}
          {canRecord && (
            <Button variant="outline" data-testid="generate-invoice-button" className="h-11" onClick={generateInvoice} disabled={busy}>
              <FileText className="h-4 w-4" /> {busy ? "Generating…" : latest ? "Generate Updated Invoice" : "Generate Invoice"}
            </Button>
          )}
          {isOwner && (
            <Button variant="outline" data-testid="apply-credit-button" className="h-11" onClick={() => setCreditOpen(true)} disabled={!credit || credit.balance <= 0}>
              Apply Credit
            </Button>
          )}
          {canRecord && (
            <Button variant="outline" data-testid="request-refund-button" className="h-11" onClick={() => setRefOpen(true)}>
              <RotateCcw className="h-4 w-4" /> Request Refund
            </Button>
          )}
        </div>
      )}

      {payments.length > 0 && (
        <div className="mt-3 space-y-1.5" data-testid="payments-list">
          {payments.map((p) => {
            const meta = PAYMENT_STATUS_META[p.status] || PAYMENT_STATUS_META.pending;
            return (
              <div key={p.id} data-testid={`payment-${p.id}`} className={`rounded-md border bg-card p-3 ${p.status === "voided" ? "opacity-60" : ""}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className={`text-sm font-bold ${p.status === "voided" ? "line-through" : ""}`}>{fmtIDR(p.amount)}</p>
                    <p className="text-[11px] font-semibold text-stone-600">
                      {PAYMENT_TYPE_LABELS[p.payment_type] || p.payment_type || "Payment"}{p.paid_at ? ` · paid ${p.paid_at}` : ""}
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      {p.method}{p.reference ? ` · ref ${p.reference}` : ""} · {p.created_by_name} · {format(new Date(p.created_at), "d MMM, HH:mm")}
                    </p>
                    {p.notes && <p className="text-[11px] text-muted-foreground">Note: {p.notes}</p>}
                    {p.status === "verified" && p.verified_at && (
                      <p className="text-[11px] text-emerald-700">verified by {p.verified_by_name} · {format(new Date(p.verified_at), "d MMM, HH:mm")}</p>
                    )}
                    {p.status === "voided" && (
                      <p className="text-[11px] font-semibold text-red-600" data-testid={`payment-void-info-${p.id}`}>
                        voided by {p.voided_by_name} · {format(new Date(p.voided_at), "d MMM, HH:mm")} · {p.void_reason}
                      </p>
                    )}
                  </div>
                  <span className={`shrink-0 rounded-md border px-2 py-0.5 text-[10px] font-bold ${meta.badge}`}>{meta.label}</span>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {p.proof_path ? (
                    <a href={fileUrl(p.proof_path)} target="_blank" rel="noreferrer" data-testid={`payment-proof-${p.id}`} className="text-[11px] font-bold text-primary underline">
                      View proof
                    </a>
                  ) : (
                    canRecord && (
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid={`payment-upload-proof-${p.id}`}
                        className="h-8 text-[11px]"
                        disabled={busy}
                        onClick={() => {
                          proofFor.current = p.id;
                          proofRef.current?.click();
                        }}
                      >
                        <ImagePlus className="h-3.5 w-3.5" /> Upload proof
                      </Button>
                    )
                  )}
                  {isOwner && p.status === "pending" && (
                    <>
                      <Button size="sm" data-testid={`payment-verify-${p.id}`} className="h-8 text-[11px]" disabled={busy} onClick={() => verifyPayment(p.id)}>
                        <BadgeCheck className="h-3.5 w-3.5" /> Verify
                      </Button>
                      <Button variant="outline" size="sm" data-testid={`payment-reject-${p.id}`} className="h-8 text-[11px] text-red-600" disabled={busy} onClick={() => rejectPayment(p.id)}>
                        <XCircle className="h-3.5 w-3.5" /> Reject
                      </Button>
                    </>
                  )}
                  {p.status !== "voided" && canRecord && (isOwner || p.created_by === user.id) && (
                    <Button variant="outline" size="sm" data-testid={`payment-correct-${p.id}`} className="h-8 text-[11px]" disabled={busy} onClick={() => openCorrection(p)}>
                      Correct
                    </Button>
                  )}
                  {p.status !== "voided" && canRecord && (isOwner || p.created_by === user.id) && (
                    <Button variant="outline" size="sm" data-testid={`payment-void-${p.id}`} className="h-8 text-[11px] text-red-600" disabled={busy} onClick={() => { setVoidingId(p.id); setVoidReason(""); }}>
                      Void entry
                    </Button>
                  )}
                </div>
                {p.pending_correction && (
                  <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2" data-testid={`payment-pending-correction-${p.id}`}>
                    <p className="text-[11px] font-bold text-amber-800">CORRECTION PENDING OWNER APPROVAL</p>
                    <p className="text-[11px] text-amber-800">
                      {fmtIDR(p.amount)} → {fmtIDR(p.pending_correction.amount)} · {p.pending_correction.method} · {p.pending_correction.paid_at}
                    </p>
                    <p className="text-[11px] text-amber-700">Reason: {p.pending_correction.reason} · by {p.pending_correction.requested_by_name}</p>
                    <p className="mt-1 text-[10px] text-amber-700">Not counted until approved — totals still use {fmtIDR(p.amount)}</p>
                    {isOwner && (
                      <div className="mt-2 flex gap-2">
                        <Button size="sm" data-testid={`correction-approve-${p.id}`} className="h-8 text-[11px]" disabled={busy} onClick={() => approveCorrection(p.id)}>
                          Approve
                        </Button>
                        <Button variant="outline" size="sm" data-testid={`correction-reject-${p.id}`} className="h-8 text-[11px] text-red-600" disabled={busy} onClick={() => { setRejectingId(p.id); setCorrRejectReason(""); }}>
                          Reject
                        </Button>
                      </div>
                    )}
                    {rejectingId === p.id && (
                      <div className="mt-2 flex gap-2">
                        <Input data-testid="correction-reject-reason" className="h-9 flex-1" placeholder="Reason (required)" value={corrRejectReason} onChange={(e) => setCorrRejectReason(e.target.value)} />
                        <Button size="sm" data-testid="correction-reject-confirm" className="h-9" disabled={busy || !corrRejectReason.trim()} onClick={rejectCorrection}>
                          Confirm
                        </Button>
                      </div>
                    )}
                  </div>
                )}
                {p.corrects_payment_id && (
                  <p className="mt-1 text-[11px] font-semibold text-rose-700" data-testid={`payment-corrected-note-${p.id}`}>
                    Corrected entry · {p.correction_reason}
                  </p>
                )}
                {correctingId === p.id && (
                  <div className="mt-2 space-y-2 rounded-md border bg-accent/30 p-3" data-testid="payment-correction-form">
                    <p className="text-[11px] text-muted-foreground">
                      Original: {fmtIDR(p.amount)} · {p.method} · {p.paid_at}
                      {isOwner ? "" : " — your correction needs Owner approval"}
                    </p>
                    <div className="space-y-1.5">
                      <Label className="text-[11px]">Corrected amount (IDR)</Label>
                      <NumInput data-testid="correction-amount" mode="money" className="h-11" value={corrForm.amount} onValue={(v) => setCorrForm({ ...corrForm, amount: v })} />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1.5">
                        <Label className="text-[11px]">Date</Label>
                        <Input type="date" data-testid="correction-date" className="h-11" value={corrForm.paid_at} onChange={(e) => setCorrForm({ ...corrForm, paid_at: e.target.value })} />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-[11px]">Type</Label>
                        <select
                          data-testid="correction-type"
                          className="h-11 w-full rounded-md border border-input bg-background px-2 text-xs font-semibold"
                          value={corrForm.payment_type}
                          onChange={(e) => setCorrForm({ ...corrForm, payment_type: e.target.value })}
                        >
                          {Object.entries(PAYMENT_TYPE_LABELS).map(([k, v]) => (
                            <option key={k} value={k}>{v}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-[11px]">Method</Label>
                      <div className="grid grid-cols-3 gap-1.5" data-testid="correction-method-options">
                        {PAYMENT_METHODS.map((m) => (
                          <button
                            key={m}
                            type="button"
                            data-testid={`correction-method-${m.toLowerCase().replace(/\s/g, "-")}`}
                            onClick={() => setCorrForm({ ...corrForm, method: m })}
                            className={`h-10 rounded-md border px-1 text-[11px] font-semibold ${corrForm.method === m ? "border-primary bg-primary/5 text-primary" : "bg-card text-stone-700"}`}
                          >
                            {m}
                          </button>
                        ))}
                      </div>
                    </div>
                    <Input data-testid="correction-reference" className="h-11" placeholder="Reference (optional)" value={corrForm.reference} onChange={(e) => setCorrForm({ ...corrForm, reference: e.target.value })} />
                    <Input data-testid="correction-reason" className="h-11" placeholder="Reason for correction (required)" value={corrForm.reason} onChange={(e) => setCorrForm({ ...corrForm, reason: e.target.value })} />
                    <div className="flex gap-2">
                      <Button size="sm" data-testid="correction-submit" className="h-10 flex-1" disabled={busy || !corrForm.reason.trim() || !(Number(corrForm.amount) > 0) || !corrForm.method} onClick={submitCorrection}>
                        {isOwner ? "Apply correction" : "Send for approval"}
                      </Button>
                      <Button variant="outline" size="sm" data-testid="correction-cancel" className="h-10" onClick={() => setCorrectingId(null)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
                {voidingId === p.id && (
                  <div className="mt-2 flex gap-2" data-testid="payment-void-form">
                    <Input data-testid="payment-void-reason" className="h-9 flex-1" placeholder="Reason (required)" value={voidReason} onChange={(e) => setVoidReason(e.target.value)} />
                    <Button size="sm" data-testid="payment-void-confirm" className="h-9" disabled={busy || !voidReason.trim()} onClick={voidPayment}>
                      Confirm void
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <input ref={proofRef} type="file" accept="image/*" className="hidden" data-testid="payment-proof-input" onChange={(e) => uploadProof(e.target.files?.[0])} />

      {invoices.length > 0 && (
        <div className="mt-3 space-y-1.5" data-testid="invoices-history">
          {latest && (
            <Button variant="outline" data-testid="view-latest-invoice" className="h-11 w-full" onClick={() => setViewInvoice(latest)}>
              <FileText className="h-4 w-4" /> View Invoice V{latest.version}
            </Button>
          )}
          {invoices.map((iv) => (
            <div key={iv.id} data-testid={`invoice-row-${iv.id}`} className="flex items-center justify-between rounded-md border bg-card p-3">
              <button className="min-w-0 text-left" data-testid={`invoice-open-${iv.id}`} onClick={() => setViewInvoice(iv)}>
                <p className="text-xs font-bold">
                  V{iv.version} {iv.version === latest?.version && <span className="text-rose-500">· LATEST</span>}
                </p>
                <p className="text-[11px] text-muted-foreground">
                  {iv.created_by_name} · {format(new Date(iv.created_at), "d MMM, HH:mm")}
                </p>
              </button>
              <div className="text-right">
                <p className="text-sm font-bold">{fmtIDR(iv.total)}</p>
                <p className={`text-[11px] font-semibold ${iv.balance_due > 0 ? "text-amber-700" : "text-emerald-700"}`}>
                  {iv.balance_due > 0 ? `due ${fmtIDR(iv.balance_due)}` : "PAID"}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {refunds.length > 0 && (
        <div className="mt-3 space-y-1.5" data-testid="refunds-list">
          {refunds.map((r) => {
            const meta = REFUND_STATUS_META[r.status] || REFUND_STATUS_META.pending;
            return (
              <div key={r.id} data-testid={`refund-${r.id}`} className="rounded-md border bg-card p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-bold">{fmtIDR(r.amount)}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {r.reason} · {r.to_credit ? "to credit" : "cash/bank"} · by {r.requested_by_name}
                    </p>
                  </div>
                  <span className={`shrink-0 rounded-md border px-2 py-0.5 text-[10px] font-bold ${meta.badge}`}>{meta.label}</span>
                </div>
                {isOwner && r.status === "pending" && (
                  <div className="mt-2 flex gap-2">
                    <Button size="sm" data-testid={`refund-approve-${r.id}`} className="h-8 text-[11px]" disabled={busy} onClick={() => approveRefund(r.id)}>
                      Approve
                    </Button>
                    <Button variant="outline" size="sm" data-testid={`refund-reject-${r.id}`} className="h-8 text-[11px] text-red-600" disabled={busy} onClick={() => setRejectingRefund(r.id)}>
                      Reject
                    </Button>
                  </div>
                )}
                {rejectingRefund === r.id && (
                  <div className="mt-2 flex gap-2">
                    <Input data-testid="refund-reject-reason" className="h-9 flex-1" placeholder="Reason (required)" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
                    <Button size="sm" data-testid="refund-reject-confirm" className="h-9" disabled={busy || !rejectReason.trim()} onClick={rejectRefund}>
                      Confirm
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Drawer open={payOpen} onOpenChange={setPayOpen} shouldScaleBackground={false}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle className="font-heading">Payment</DrawerTitle>
          </DrawerHeader>
          <div className="max-h-[70vh] space-y-3 overflow-y-auto overscroll-contain px-4 pb-8" data-vaul-no-drag data-testid="payment-form">
            <p className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-[11px] font-semibold text-emerald-800">
              Recorded — Finance Checked Externally. No in-app verification needed.
            </p>
            <div className="space-y-1.5">
              <Label>Amount (IDR) *</Label>
              <NumInput data-testid="payment-amount" mode="money" className="h-12" value={payForm.amount} onValue={(v) => { setDupWarning(""); setPayForm((f) => ({ ...f, amount: v })); }} />
            </div>
            <div className="space-y-1.5">
              <Label>Payment date *</Label>
              <Input type="date" data-testid="payment-date" className="h-12" value={payForm.paid_at} onChange={(e) => { setDupWarning(""); setPayForm((f) => ({ ...f, paid_at: e.target.value })); }} />
            </div>
            <div className="space-y-1.5">
              <Label>Type *</Label>
              <div className="grid grid-cols-1 gap-2" data-testid="payment-type-options">
                {Object.entries(PAYMENT_TYPE_LABELS).map(([k, v]) => (
                  <button
                    key={k}
                    type="button"
                    data-testid={`payment-type-${k.toLowerCase()}`}
                    onClick={() => { setDupWarning(""); setPayForm((f) => ({ ...f, payment_type: k })); }}
                    className={`h-12 rounded-md border px-4 text-left text-sm font-semibold transition-colors active:scale-[0.99] ${
                      payForm.payment_type === k ? "border-primary bg-primary/5 text-primary" : "bg-card text-stone-700"
                    }`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Method *</Label>
              <div className="grid grid-cols-1 gap-2" data-testid="payment-method-options">
                {PAYMENT_METHODS.map((m) => (
                  <button
                    key={m}
                    type="button"
                    data-testid={`payment-method-${m.toLowerCase().replace(/\s/g, "-")}`}
                    onClick={() => setPayForm((f) => ({ ...f, method: m }))}
                    className={`h-12 rounded-md border px-4 text-left text-sm font-semibold transition-colors active:scale-[0.99] ${
                      payForm.method === m ? "border-primary bg-primary/5 text-primary" : "bg-card text-stone-700"
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Reference / proof no.</Label>
              <Input data-testid="payment-reference" className="h-12" placeholder="Transfer ref / note" value={payForm.reference} onChange={(e) => { setDupWarning(""); setPayForm((f) => ({ ...f, reference: e.target.value })); }} />
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Input data-testid="payment-notes" className="h-12" placeholder="Optional notes" value={payForm.notes} onChange={(e) => setPayForm((f) => ({ ...f, notes: e.target.value }))} />
            </div>
            {dupWarning && (
              <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2" data-testid="payment-duplicate-warning">
                <p className="text-[11px] font-semibold text-amber-800">{dupWarning}</p>
                <Button variant="outline" size="sm" data-testid="payment-confirm-duplicate" className="mt-2 h-9 w-full text-[11px]" disabled={busy} onClick={() => recordPayment(true)}>
                  Yes, record it anyway
                </Button>
              </div>
            )}
            <Button data-testid="payment-submit" className="h-12 w-full" onClick={() => recordPayment(false)} disabled={busy || !(Number(payForm.amount) > 0) || !payForm.method.trim() || !payForm.paid_at}>
              Save Payment
            </Button>
          </div>
        </DrawerContent>
      </Drawer>

      <Drawer open={shipOpen} onOpenChange={setShipOpen} shouldScaleBackground={false}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle className="font-heading">Shipping Fee</DrawerTitle>
          </DrawerHeader>
          <div className="space-y-3 px-4 pb-8" data-testid="initial-shipping-form">
            <p className="text-xs text-muted-foreground">
              Charged to the customer separately from product prices. Can be adjusted again at Final Invoice.
            </p>
            <div className="space-y-1.5">
              <Label>Amount (IDR)</Label>
              <NumInput data-testid="initial-shipping-amount" mode="money" className="h-12" value={shipAmt} onValue={setShipAmt} />
            </div>
            <Button data-testid="initial-shipping-submit" className="h-12 w-full" onClick={saveInitialShipping} disabled={busy}>
              Save Shipping Charge
            </Button>
          </div>
        </DrawerContent>
      </Drawer>

      <Drawer open={creditOpen} onOpenChange={setCreditOpen} shouldScaleBackground={false}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle className="font-heading">Apply Customer Credit</DrawerTitle>
          </DrawerHeader>
          <div className="space-y-3 px-4 pb-8" data-testid="apply-credit-form">
            <p className="text-xs text-muted-foreground">
              Available balance: <span className="font-bold text-rose-700">{fmtIDR(credit?.balance || 0)}</span>
            </p>
            <div className="space-y-1.5">
              <Label>Amount (IDR) *</Label>
              <NumInput data-testid="apply-credit-amount" mode="money" className="h-12" value={creditAmt} onValue={setCreditAmt} />
            </div>
            <Button data-testid="apply-credit-submit" className="h-12 w-full" onClick={applyCredit} disabled={busy || !(Number(creditAmt) > 0) || Number(creditAmt) > (credit?.balance || 0)}>
              Apply to This Order
            </Button>
          </div>
        </DrawerContent>
      </Drawer>

      <Drawer open={refOpen} onOpenChange={setRefOpen} shouldScaleBackground={false}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle className="font-heading">Request Refund</DrawerTitle>
          </DrawerHeader>
          <div className="space-y-3 px-4 pb-8" data-testid="refund-form">
            <div className="space-y-1.5">
              <Label>Amount (IDR) *</Label>
              <NumInput data-testid="refund-amount" mode="money" className="h-12" value={refForm.amount} onValue={(v) => setRefForm({ ...refForm, amount: v })} />
            </div>
            <div className="space-y-1.5">
              <Label>Reason *</Label>
              <Input data-testid="refund-reason" className="h-12" value={refForm.reason} onChange={(e) => setRefForm({ ...refForm, reason: e.target.value })} />
            </div>
            <div className="flex items-center justify-between rounded-md border bg-card px-3 py-2.5">
              <Label htmlFor="refund-to-credit" className="text-sm font-semibold">Refund to customer credit</Label>
              <Switch id="refund-to-credit" data-testid="refund-to-credit" checked={refForm.to_credit} onCheckedChange={(v) => setRefForm({ ...refForm, to_credit: v })} />
            </div>
            <Button data-testid="refund-submit" className="h-12 w-full" onClick={requestRefund} disabled={busy || !(Number(refForm.amount) > 0) || !refForm.reason.trim()}>
              Submit Refund Request
            </Button>
          </div>
        </DrawerContent>
      </Drawer>

      <InvoiceViewer invoice={viewInvoice} open={!!viewInvoice} onOpenChange={(o) => !o && setViewInvoice(null)} />

      {credit && credit.entries.length > 0 && (        <div className="mt-3" data-testid="credit-ledger">
          <p className="mb-1.5 text-xs font-bold uppercase tracking-wide text-muted-foreground">Customer Credit Ledger</p>
          <div className="space-y-1.5">
            {credit.entries.slice(0, 10).map((e) => (
              <div key={e.id} className="flex items-center justify-between rounded-md border bg-card p-2.5">
                <div className="min-w-0">
                  <p className="text-xs font-semibold">{CREDIT_TYPE_LABELS[e.transaction_type] || e.transaction_type}</p>
                  <p className="truncate text-[11px] text-muted-foreground">
                    {e.reason} · {e.created_by_name} · {format(new Date(e.created_at), "d MMM, HH:mm")}
                  </p>
                </div>
                <span className={`shrink-0 text-sm font-bold ${e.direction === "in" ? "text-emerald-700" : "text-red-600"}`}>
                  {e.direction === "in" ? "+" : "−"}{fmtIDR(e.amount)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
